import axios from 'axios'
import proj4 from 'proj4'

export type RasterRender = {
	dataUrl: string
	bounds: [number, number, number, number]
}

type Bbox = [number, number, number, number]

const ensureProj4Def = (epsg: number): string | null => {
	const code = `EPSG:${epsg}`
	if (proj4.defs(code)) return code

	if (epsg >= 32601 && epsg <= 32660) {
		const zone = epsg - 32600
		proj4.defs(code, `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs`)
		return code
	}
	if (epsg >= 32701 && epsg <= 32760) {
		const zone = epsg - 32700
		proj4.defs(code, `+proj=utm +zone=${zone} +south +datum=WGS84 +units=m +no_defs`)
		return code
	}
	// SIRGAS 2000 / UTM north — EPSG 31965..31976 → zones 11N..22N
	if (epsg >= 31965 && epsg <= 31976) {
		const zone = epsg - 31954
		proj4.defs(
			code,
			`+proj=utm +zone=${zone} +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`,
		)
		return code
	}
	// SIRGAS 2000 / UTM south — EPSG 31977..31985 → zones 17S..25S
	if (epsg >= 31977 && epsg <= 31985) {
		const zone = epsg - 31960
		proj4.defs(
			code,
			`+proj=utm +zone=${zone} +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`,
		)
		return code
	}
	return null
}

const reprojectBboxToLatLng = (bbox: Bbox, epsg: number): Bbox | null => {
	const code = ensureProj4Def(epsg)
	if (!code) return null

	const transformer = proj4(code, 'EPSG:4326')
	const [minX, minY, maxX, maxY] = bbox
	const corners: Array<[number, number]> = [
		[minX, minY],
		[maxX, minY],
		[maxX, maxY],
		[minX, maxY],
	]
	const lonLat = corners.map((c) => transformer.forward(c))
	const lons = lonLat.map((p) => p[0])
	const lats = lonLat.map((p) => p[1])
	return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)]
}

// Mesma escala que pinta os plantios no heatmap vetorial (coresPlantios).
// Direcao: menor valor -> vermelho (#fe0201), maior valor -> verde (#385622).
const PRODUCTIVITY_SCALE = [
	[254, 2, 1], // #fe0201
	[249, 219, 105], // #f9db69
	[166, 208, 142], // #a6d08e
	[56, 86, 34], // #385622
]

const interpolateColor = (t: number): [number, number, number] => {
	const clamped = Math.max(0, Math.min(1, t))
	const scaled = clamped * (PRODUCTIVITY_SCALE.length - 1)
	const lower = Math.floor(scaled)
	const upper = Math.min(PRODUCTIVITY_SCALE.length - 1, lower + 1)
	const f = scaled - lower
	const a = PRODUCTIVITY_SCALE[lower]
	const b = PRODUCTIVITY_SCALE[upper]
	return [
		Math.round(a[0] + (b[0] - a[0]) * f),
		Math.round(a[1] + (b[1] - a[1]) * f),
		Math.round(a[2] + (b[2] - a[2]) * f),
	]
}

const findBandIndexByLayerIdentifier = async (
	image: any,
	bandCount: number,
	layerIdentifier: string,
): Promise<number> => {
	const target = layerIdentifier.toLowerCase()
	const descriptions: string[] = []
	for (let i = 0; i < bandCount; i++) {
		const gdal = (await image.getGDALMetadata(i)) ?? {}
		const description = (gdal.DESCRIPTION as string | undefined) ?? ''
		descriptions.push(description)
		if (description.toLowerCase() === target) return i
	}
	throw new Error(
		`Banda "${layerIdentifier}" não encontrada no TIFF. Descrições disponíveis: [${descriptions.join(', ')}]`,
	)
}

export class GeoTiffRasterRenderer {
	static async fetchAndRender(blobUrl: string, bandLayerIdentifier: string): Promise<RasterRender> {
		const response = await axios.get<ArrayBuffer>(blobUrl, { responseType: 'arraybuffer' })
		const buffer = response.data

		const { fromArrayBuffer } = await import('geotiff')
		const tiff = await fromArrayBuffer(buffer)
		const image = await tiff.getImage(0)

		const rawBbox = image.getBoundingBox() as Bbox
		if (!rawBbox || rawBbox.some((v) => !Number.isFinite(v))) {
			throw new Error('TIFF não possui georreferenciamento válido (bbox ausente)')
		}

		const geoKeys = (image.getGeoKeys?.() ?? {}) as Record<string, number | undefined>
		const epsg = geoKeys.ProjectedCSTypeGeoKey ?? geoKeys.GeographicTypeGeoKey ?? null

		let bbox: Bbox = rawBbox
		if (epsg && epsg !== 4326) {
			const reprojected = reprojectBboxToLatLng(rawBbox, epsg)
			if (!reprojected) {
				throw new Error(`Não foi possível reprojetar o bbox do TIFF: EPSG ${epsg} não suportado`)
			}
			bbox = reprojected
		}

		const width = image.getWidth()
		const height = image.getHeight()
		const bandCount = image.getSamplesPerPixel()
		const noDataValue = image.getGDALNoData()

		const bandIndex = await findBandIndexByLayerIdentifier(image, bandCount, bandLayerIdentifier)

		const rasters = await image.readRasters({ samples: [bandIndex] })
		const data = rasters[0] as Float32Array | Uint8Array | Int16Array

		let min = Infinity
		let max = -Infinity
		for (let i = 0; i < data.length; i++) {
			const v = data[i]
			if (noDataValue !== null && v === noDataValue) continue
			if (!Number.isFinite(v)) continue
			if (v < min) min = v
			if (v > max) max = v
		}
		if (min === Infinity || max === -Infinity || min === max) {
			throw new Error('TIFF sem valores numéricos suficientes para renderizar')
		}

		const canvas = document.createElement('canvas')
		canvas.width = width
		canvas.height = height
		const ctx = canvas.getContext('2d')
		if (!ctx) throw new Error('Não foi possível obter contexto 2D do canvas')
		const imageData = ctx.createImageData(width, height)
		const pixels = imageData.data
		const range = max - min

		for (let i = 0; i < data.length; i++) {
			const v = data[i]
			const offset = i * 4
			if ((noDataValue !== null && v === noDataValue) || !Number.isFinite(v)) {
				pixels[offset + 3] = 0
				continue
			}
			const t = (v - min) / range
			const [r, g, b] = interpolateColor(t)
			pixels[offset] = r
			pixels[offset + 1] = g
			pixels[offset + 2] = b
			pixels[offset + 3] = 200
		}
		ctx.putImageData(imageData, 0, 0)

		return {
			dataUrl: canvas.toDataURL('image/png'),
			bounds: bbox,
		}
	}
}
